import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../api/client';
import EditRutaPanel from '../shared/EditRutaPanel';

/**
 * Panel derecho del módulo de Sesgos.
 * Muestra los cruces con Estatus='CasetaNoEncontradaEnRuta' de una ruta y permite:
 *   1. Vincular el nombre PASE a una caseta existente de la ruta (Cat_EntidadCaseta)
 *   2. Abrir EditRutaPanel para agregar la caseta a la ruta
 *
 * Props:
 *   - ruta    {Object}    Ruta seleccionada (con id_Tipo_ruta, RazonOrigen, RazonDestino, etc.)
 *   - onClose {Function}  Callback para deseleccionar la ruta
 */
function CrucesSesgosPanel({ ruta, onClose }) {
    const [cruces, setCruces] = useState([]);
    const [casetas, setCasetas] = useState([]);   // casetas actuales de la ruta
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Selecciones inline: { [nombrePASE]: id_caseta_seleccionada }
    const [vinculos, setVinculos] = useState({});
    // Cruces guardando (Set de nombre_pase)
    const [savingVinculos, setSavingVinculos] = useState(new Set());
    // Cruces que ya fueron vinculados en esta sesión
    const [resueltosLocal, setResueltosLocal] = useState(new Set());

    const [showEditRuta, setShowEditRuta] = useState(false);
    const [toast, setToast] = useState(null); // { type: 'success'|'danger', msg }

    const cargarDatos = useCallback(() => {
        setLoading(true);
        setError(null);
        Promise.all([
            apiClient.get(`/api/sesgos/rutas/${ruta.id_Tipo_ruta}/cruces`),
            apiClient.get(`/api/casetas/rutas/${ruta.id_Tipo_ruta}/casetasPorRuta`),
        ])
            .then(([resCruces, resCasetas]) => {
                setCruces(resCruces.data);
                setCasetas(resCasetas.data);
            })
            .catch(() => setError('Error al cargar los datos de esta ruta.'))
            .finally(() => setLoading(false));
    }, [ruta.id_Tipo_ruta]);

    useEffect(() => {
        cargarDatos();
    }, [cargarDatos]);

    const showToast = (type, msg) => {
        setToast({ type, msg });
        setTimeout(() => setToast(null), 4000);
    };

    const handleVincular = async (nombre_pase, id_caseta) => {
        if (!id_caseta) return;
        setSavingVinculos(prev => new Set([...prev, nombre_pase]));
        try {
            const res = await apiClient.post('/api/sesgos/casetas/vincular', {
                nombre_pase,
                id_caseta: Number(id_caseta),
                id_tipo_ruta: ruta.id_Tipo_ruta,
            });
            showToast('success', res.data.message);
            // Marcar todos los cruces con ese nombre como resueltos localmente
            setResueltosLocal(prev => {
                const next = new Set(prev);
                cruces.filter(c => c.Caseta === nombre_pase).forEach(c => next.add(c.ID));
                return next;
            });
            setVinculos(prev => { const n = { ...prev }; delete n[nombre_pase]; return n; });
        } catch {
            showToast('danger', 'Error al guardar el vínculo. Intenta nuevamente.');
        } finally {
            setSavingVinculos(prev => { const n = new Set(prev); n.delete(nombre_pase); return n; });
        }
    };

    // Agrupar cruces por nombre PASE (misma caseta puede tener varios cruces)
    const crucesAgrupados = React.useMemo(() => {
        const map = {};
        cruces.forEach(c => {
            if (!map[c.Caseta]) map[c.Caseta] = { nombre_pase: c.Caseta, cruces: [], ya_vinculada: !!c.nombre_vinculado, id_caseta_vinculada: c.id_caseta_vinculada };
            map[c.Caseta].cruces.push(c);
        });
        return Object.values(map).sort((a, b) => b.cruces.length - a.cruces.length);
    }, [cruces]);

    const pendientes = crucesAgrupados.filter(g => !resueltosLocal.has(g.cruces[0]?.ID) && !g.cruces.every(c => resueltosLocal.has(c.ID)));
    const resueltos = crucesAgrupados.filter(g => g.cruces.every(c => resueltosLocal.has(c.ID)) || g.ya_vinculada);
    const totalPendientes = cruces.filter(c => !resueltosLocal.has(c.ID)).length;

    return (
        <div className="card shadow h-100" style={{ border: '2px solid #17a2b8', borderRadius: '0.5rem' }}>
            {/* Cabecera */}
            <div className="card-header bg-info d-flex align-items-center justify-content-between py-2">
                <div>
                    <h6 className="m-0 font-weight-bold text-white">
                        <i className="fas fa-unlink mr-2"></i>
                        Cruces sin caseta en ruta: <span className="font-weight-normal">{ruta.id_Tipo_ruta}</span>
                    </h6>
                    <small className="text-white" style={{ opacity: 0.85, fontSize: '0.75rem' }}>
                        {ruta.RazonOrigen || ruta.PoblacionOrigen}
                        <i className="fas fa-arrow-right mx-1"></i>
                        {ruta.RazonDestino || ruta.PoblacionDestino}
                    </small>
                </div>
                <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                    {casetas.length > 0 && (
                        <button
                            className="btn btn-sm btn-warning"
                            onClick={() => setShowEditRuta(true)}
                            title="Agregar caseta nueva a la ruta"
                        >
                            <i className="fas fa-plus mr-1"></i> Editar ruta
                        </button>
                    )}
                    <button type="button" className="close text-white" style={{ opacity: 1 }} onClick={onClose}>
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            </div>

            {/* Toast */}
            {toast && (
                <div className={`alert alert-${toast.type} mb-0 py-2 px-3 border-0`} style={{ borderRadius: 0, fontSize: '0.85rem' }}>
                    <i className={`fas fa-${toast.type === 'success' ? 'check-circle' : 'exclamation-circle'} mr-2`}></i>
                    {toast.msg}
                </div>
            )}

            {/* Barra de progreso */}
            {!loading && cruces.length > 0 && (
                <div className="px-3 pt-2 pb-1">
                    <div className="d-flex justify-content-between small text-muted mb-1">
                        <span>{resueltosLocal.size} cruce(s) resueltos en esta sesión</span>
                        <span>{totalPendientes} pendiente(s) de un total de {cruces.length}</span>
                    </div>
                    <div className="progress" style={{ height: '6px' }}>
                        <div
                            className="progress-bar bg-success"
                            style={{ width: `${cruces.length > 0 ? (resueltosLocal.size / cruces.length) * 100 : 0}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Cuerpo */}
            <div className="card-body p-2" style={{ overflow: 'auto', flex: 1 }}>
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-info" role="status" />
                        <p className="mt-2 text-muted small">Cargando cruces...</p>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger">{error}</div>
                ) : cruces.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fas fa-check-circle fa-2x text-success mb-2"></i>
                        <p>Sin cruces pendientes para esta ruta.</p>
                    </div>
                ) : (
                    <>
                        {/* ── Grupos pendientes ── */}
                        {pendientes.map(grupo => {
                            const nombre = grupo.nombre_pase;
                            const isSaving = savingVinculos.has(nombre);
                            const selectedCaseta = vinculos[nombre] || '';

                            return (
                                <div
                                    key={nombre}
                                    className="card mb-2 border-warning"
                                    style={{ fontSize: '0.82rem' }}
                                >
                                    <div className="card-header py-1 d-flex align-items-center justify-content-between bg-warning-light" style={{ backgroundColor: '#fff9e6' }}>
                                        <div>
                                            <strong className="text-dark">
                                                <i className="fas fa-road mr-1 text-warning"></i>
                                                {nombre}
                                            </strong>
                                            <span className="badge badge-warning ml-2">{grupo.cruces.length} cruce(s)</span>
                                            {grupo.ya_vinculada && (
                                                <span className="badge badge-info ml-1" title="Ya tiene un vínculo guardado">
                                                    <i className="fas fa-link"></i> Vinculada
                                                </span>
                                            )}
                                        </div>
                                        <small className="text-muted">
                                            ${grupo.cruces.reduce((s, c) => s + parseFloat(c.Importe || 0), 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} total
                                        </small>
                                    </div>

                                    {/* Mini-tabla de cruces */}
                                    <div className="card-body p-0">
                                        <table className="table table-sm mb-0" style={{ fontSize: '0.78rem' }}>
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>Fecha</th>
                                                    <th>OT</th>
                                                    <th>Clase</th>
                                                    <th className="text-right">Importe</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {grupo.cruces.slice(0, 5).map(c => (
                                                    <tr key={c.ID}>
                                                        <td className="text-nowrap">
                                                            {c.Fecha ? new Date(c.Fecha).toLocaleString('es-MX', { timeZone: 'America/Mexico_City', dateStyle: 'short', timeStyle: 'short' }) : '-'}
                                                        </td>
                                                        <td className="text-muted">{c.id_orden || '—'}</td>
                                                        <td>{c.Clase || '—'}</td>
                                                        <td className="text-right font-weight-bold">
                                                            ${parseFloat(c.Importe || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {grupo.cruces.length > 5 && (
                                                    <tr>
                                                        <td colSpan="4" className="text-center text-muted small py-1">
                                                            ... y {grupo.cruces.length - 5} más
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Acción: vincular a caseta existente */}
                                    <div className="card-footer py-2 bg-light d-flex align-items-center" style={{ gap: '6px' }}>
                                        <select
                                            className="form-control form-control-sm"
                                            value={selectedCaseta}
                                            onChange={e => setVinculos(prev => ({ ...prev, [nombre]: e.target.value }))}
                                            disabled={isSaving}
                                        >
                                            <option value="">— Vincular a caseta de la ruta —</option>
                                            {casetas.map(cas => (
                                                <option key={cas.ID_Caseta} value={cas.ID_Caseta}>
                                                    {cas.Nombre_IAVE || cas.Nombre || `Caseta #${cas.ID_Caseta}`}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            className="btn btn-sm btn-info text-white"
                                            style={{ whiteSpace: 'nowrap' }}
                                            onClick={() => handleVincular(nombre, selectedCaseta)}
                                            disabled={!selectedCaseta || isSaving}
                                            title="Guardar vínculo y actualizar cruces"
                                        >
                                            {isSaving
                                                ? <i className="fas fa-spinner fa-spin"></i>
                                                : <><i className="fas fa-link mr-1"></i>Vincular</>
                                            }
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        {/* ── Grupos ya resueltos ── */}
                        {resueltos.length > 0 && (
                            <details className="mt-2">
                                <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                                    <i className="fas fa-check-circle text-success mr-1"></i>
                                    {resueltos.length} nombre(s) ya vinculados
                                </summary>
                                <div className="mt-1">
                                    {resueltos.map(g => (
                                        <div key={g.nombre_pase} className="card mb-1 border-success" style={{ fontSize: '0.78rem' }}>
                                            <div className="card-body py-1 px-2 d-flex align-items-center justify-content-between">
                                                <span className="text-success font-weight-bold">
                                                    <i className="fas fa-check mr-1"></i>{g.nombre_pase}
                                                </span>
                                                <span className="badge badge-success">{g.cruces.length} cruce(s)</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </details>
                        )}
                    </>
                )}
            </div>

            {/* Panel flotante de edición de ruta */}
            {showEditRuta && (
                <EditRutaPanel
                    id_tipo_ruta={ruta.id_Tipo_ruta}
                    initialCasetas={casetas}
                    onClose={() => setShowEditRuta(false)}
                    onSaved={() => {
                        setShowEditRuta(false);
                        cargarDatos();
                    }}
                />
            )}
        </div>
    );
}

export default CrucesSesgosPanel;
